import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  FormControl,
  InputLabel,
  TextField,
  TextareaAutosize,
  Stack,
  Typography,
  IconButton,
  Select,
  MenuItem
} from '@mui/material';
import { Add as AddIcon, Delete as DeleteIcon } from '@mui/icons-material';
import { useCreateItemMutation, useUpdatePropertyMutation, useGetItemsQuery } from '../store/docApi';

function QuestForm({ quest, onClose }) {
  const [formData, setFormData] = useState({
    id: '',
    name: '',
    description: '',
    tripId: '',
    steps: [{ description: '', status: 'pending' }]
  });

  const { data: trips } = useGetItemsQuery('trip');
  const [createQuest] = useCreateItemMutation();
  const [updateProperty] = useUpdatePropertyMutation();

  useEffect(() => {
    if (quest) {
      setFormData({
        label: 'quest',
        id: quest.questId || '',
        name: quest.name || '',
        description: quest.description || '',
        tripId: quest.tripId || '',
        steps: quest.steps || [{ description: '', status: 'pending' }]
      });
    }
  }, [quest]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value
    }));
  };

  const handleStepChange = (index, field, value) => {
    setFormData((prev) => ({
      ...prev,
      steps: prev.steps.map((step, i) => (i === index ? { ...step, [field]: value } : step))
    }));
  };

  const addStep = () => {
    setFormData((prev) => ({
      ...prev,
      steps: [...prev.steps, { description: '', status: 'pending' }]
    }));
  };

  const removeStep = (index) => {
    setFormData((prev) => ({
      ...prev,
      steps: prev.steps.filter((_, i) => i !== index)
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (quest) {
        // Update each property individually
        const updates = Object.entries(formData).map(([name, value]) =>
          updateProperty({
            label: 'quest',
            id: quest.id,
            name,
            value
          }).unwrap()
        );
        await Promise.all(updates);
      } else {
        await createQuest({ ...formData }).unwrap();
      }
      onClose();
    } catch (error) {
      console.error('Failed to save quest:', error);
    }
  };

  return (
    <Box component="form" onSubmit={handleSubmit}>
      <Stack spacing={2}>
        <Typography variant="h5">{quest ? 'Edit Quest' : 'Create New Quest'}</Typography>

        <FormControl fullWidth>
          <InputLabel shrink htmlFor="quest-id">
            Quest ID
          </InputLabel>
          <TextField id="quest-id" name="id" value={formData.id} onChange={handleChange} required variant="outlined" />
        </FormControl>

        <FormControl fullWidth>
          <InputLabel shrink htmlFor="quest-name">
            Quest Name
          </InputLabel>
          <TextField id="quest-name" name="name" value={formData.name} onChange={handleChange} required variant="outlined" />
        </FormControl>

        <FormControl fullWidth>
          <InputLabel shrink htmlFor="quest-description">
            Description
          </InputLabel>
          <TextareaAutosize
            id="quest-description"
            name="description"
            value={formData.description}
            onChange={handleChange}
            minRows={3}
            style={{
              width: '100%',
              padding: '8px',
              borderRadius: '4px',
              borderColor: '#ccc'
            }}
          />
        </FormControl>

        <FormControl fullWidth>
          <InputLabel id="trip-select-label">Associated Trip</InputLabel>
          <Select labelId="trip-select-label" name="tripId" value={formData.tripId} onChange={handleChange}>
            <MenuItem value="">None</MenuItem>
            {trips?.map((trip) => (
              <MenuItem key={trip.id} value={trip.id}>
                {trip.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <Typography variant="h6" sx={{ mt: 2 }}>
          Quest Steps
        </Typography>
        {formData.steps.map((step, index) => (
          <Box key={index} sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
            <FormControl sx={{ flex: 1 }}>
              <InputLabel shrink htmlFor={`step-description-${index}`}>
                Step {index + 1}
              </InputLabel>
              <TextField
                id={`step-description-${index}`}
                value={step.description}
                onChange={(e) => handleStepChange(index, 'description', e.target.value)}
                placeholder="Step description"
                variant="outlined"
              />
            </FormControl>
            <FormControl sx={{ width: 150 }}>
              <InputLabel id={`step-status-label-${index}`}>Status</InputLabel>
              <Select
                labelId={`step-status-label-${index}`}
                value={step.status}
                onChange={(e) => handleStepChange(index, 'status', e.target.value)}
              >
                <MenuItem value="pending">Pending</MenuItem>
                <MenuItem value="in_progress">In Progress</MenuItem>
                <MenuItem value="completed">Completed</MenuItem>
              </Select>
            </FormControl>
            <IconButton color="error" onClick={() => removeStep(index)} sx={{ mt: 2 }}>
              <DeleteIcon />
            </IconButton>
          </Box>
        ))}

        <Button startIcon={<AddIcon />} variant="outlined" onClick={addStep}>
          Add Step
        </Button>

        <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
          <Button variant="outlined" color="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" variant="contained" color="primary">
            {quest ? 'Update Quest' : 'Create Quest'}
          </Button>
        </Box>
      </Stack>
    </Box>
  );
}

export default QuestForm;
